import {Transaction} from "../../../../domain/transaction"
import React, {useContext, useEffect, useState} from "react"
import {Modal} from "../../../../components-shared/modal/modal"
import {HeadingMedium, HeadingSmall} from "../../../../components-shared/heading/heading"
import {CategoryHelper, CategoryHelperContext} from "../../../../services/helpers/category-helper"
import {Button} from "../../../../components-shared/button/button"
import {ListedValue} from "../../../../components-shared/value-list/listed-value"
import {ValueList} from "../../../../components-shared/value-list/value-list"
import styles from "./transaction-modal.module.scss"
import {useServices} from "../../../../services/services"
import {getApiCallErrorMessageHandler} from "../../../../services/api/api-utils"
import {getInitialTransactionsSettings, TransactionsSettings} from "../../../../services/api/transactions-api"
import {GetEmptyTransactionsWithCount, TransactionsWithCount} from "../../../../domain/transactions-with-count"
import {TransactionsList} from "../transactions-list"
import {FormCategorySelect} from "../form-category-select/form-category-select"
import {Panel} from "../../../../components-shared/panel/panel"
import {Checkbox} from "../../../../components-shared/checkbox/checkbox"
import {FormTextInput} from "../../../../components-shared/forms/form-text-input/form-text-input-view"
import {TextMedium, TextSmall} from "../../../../components-shared/text/text"
import {TransactionAmount} from "../transaction-amount/transaction-amount"
import {FormattedDate} from "../../../../components-shared/date/formatted-date"
import {Rule} from "../../../../domain/rule"
import {ValidationResult, ValidResult} from "../../../../util/validation/validation-result"
import {RadioButton, RadioButtonGroup} from "../../../../components-shared/radio-button/radiobutton"

interface TransactionModalProps {
  transaction: Transaction | null
  closeModal: () => void
  saveAndCloseModal: () => void
}

const DefaultRule: Rule = {priority: 0}
type ExistingRuleOption = "DeleteExistingRule" | "AddHigherPriorityRule"

export const TransactionModal : React.FC<TransactionModalProps> = ({ transaction, closeModal, saveAndCloseModal }) => {
  const categoryHelper = useContext<CategoryHelper | null>(CategoryHelperContext)
  const [showEditCategory, setShowEditCategory] = useState<boolean>(false)
  const [transactionsSettings, setTransactionsSettings] = useState<TransactionsSettings | null>(null)
  const [transactionsWithCount, setTransactionsWithCount] = useState<TransactionsWithCount | null>(null)
  const [ruleUsed, setRuleUsed] = useState<Rule | null>(null)
  const [rule, setRule] = useState<Rule>(DefaultRule)
  const [existingRuleOption, setExistingRuleOption] = useState<ExistingRuleOption>("DeleteExistingRule")

  const [applyToAll, setApplyToAll] = useState<boolean>(true)
  const [ruleDescriptionValidationResult, setRuleDescriptionValidationResult] = useState<ValidationResult>(ValidResult)
  const [categoryIdValidationResult, setCategoryIdValidationResult] = useState<ValidationResult>(ValidResult)
  const services = useServices()
  const controller = new AbortController()

  useEffect(() => {
    if (transaction === null) return

    // if the input is changed, the settings should be overwritten
    setTransactionsSettings({...getInitialTransactionsSettings(), name: transaction.name})
    setRule({...DefaultRule, categoryId: transaction.categoryId, description: transaction.name})
  }, [transaction])

  useEffect(() => {
    if (services === null || transaction === null) return

    if (transaction.categorizedUsingRuleId === undefined) {
      if (ruleUsed !== null) setRuleUsed(null)
      return
    }

    const apiCallErrorHandler = getApiCallErrorMessageHandler()
    services.rulesApi.getRuleForTransaction(transaction.id, controller.signal)
      .then(setRuleUsed)
      .catch(apiCallErrorHandler)

    return () => controller.abort()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, transaction])

  useEffect(() => {
    if (services === null || transactionsSettings === null) return

    if (!ruleDescriptionValidationResult.valid) {
      setTransactionsWithCount(GetEmptyTransactionsWithCount())
      return
    }

    const apiCallErrorHandler = getApiCallErrorMessageHandler()
    services.transactionsApi.getTransactions(transactionsSettings, controller.signal)
      .then(setTransactionsWithCount)
      .catch(apiCallErrorHandler)

    return () => controller.abort()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, transactionsSettings])

  useEffect(() => {
    if (!showEditCategory) setShowEditCategory(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction])

  if (categoryHelper === null || transactionsSettings === null || services === null) return null
  if (transaction === null) return <Modal show={false}/> // NOTE: if we do not return anything, overflow: hidden will remain and the scroll bar will disappear

  const otherTransactions = transactionsWithCount?.transactions.filter(t => t.id !== transaction.id)
  const isPartOfTransactionsFound = otherTransactions !== undefined && transactionsWithCount !== null && otherTransactions.length < transactionsWithCount.transactions.length
  const categoryName = categoryHelper.getCategoryName(transaction.categoryId)

  const openEditCategory = () => {
    if (!applyToAll) setApplyToAll(true)
    if (existingRuleOption === "AddHigherPriorityRule") setExistingRuleOption("DeleteExistingRule")
    setRule({...DefaultRule, categoryId: transaction.categoryId, description: transaction.name})
    setShowEditCategory(true)
  }

  const cancelEditCategory = () => {
    if (transactionsSettings.name !== transaction.name) setTransactionsSettings({...transactionsSettings, name: transaction.name})
    setShowEditCategory(false)
  }

  const setName = (name: string) => {
    setTransactionsSettings({...transactionsSettings, name, unlimitedTransactions: false})
    setRule({...rule, description: name})

    const validationResult: ValidationResult = {valid: (name !== "" && name.length > 1)}
    if (!validationResult.valid) {
      if (name === "") validationResult.errorMessage = "Dit is een verplicht veld"
      else if (name.length <= 1) validationResult.errorMessage = "Vul alstublieft tenminste 2 karakters in"
    }

    setRuleDescriptionValidationResult(validationResult)
  }

  const setCategoryId = (categoryId: number) => {
    setRule({...rule, categoryId})

    const validationResult: ValidationResult = {valid: categoryId !== null, errorMessage: categoryId !== null ? "Dit is een verplicht veld" : undefined}

    setCategoryIdValidationResult(validationResult)
  }

  const clickExistingRuleRadioButton = () => {
    if (existingRuleOption === "DeleteExistingRule") {
      setRule({...rule, priority: (ruleUsed?.priority || 0) + 1})
      setExistingRuleOption("AddHigherPriorityRule")
    } else {
      setRule({...rule, priority: 0})
      setExistingRuleOption("DeleteExistingRule")
    }
  }

  const clickLoadAll = () => {
    setTransactionsSettings({...transactionsSettings, unlimitedTransactions: true})
  }

  const clickApply = async () => {
    if (rule.categoryId === undefined) throw new Error("categoryId must be set before saving the transaction")

    if (applyToAll) {
      if (existingRuleOption === "DeleteExistingRule" && ruleUsed !== null && ruleUsed.id !== undefined && transaction.categorizationTypeAsEnum !== "SystemRules") await services.rulesApi.deleteRule(ruleUsed.id!)
      await services.rulesApi.saveRule(rule)
      if (transaction.categorizationTypeAsEnum === "Manually") {
        transaction.categoryId = rule.categoryId
        await services.transactionsApi.updateTransaction(transaction) // Also change the transaction because apply will not change this transaction
      }
    } else {
      transaction.categoryId = rule.categoryId
      await services.transactionsApi.updateTransaction(transaction)
    }
    saveAndCloseModal()
  }

  const transactionAmountReducer = (accumulator: number, currentTransaction: Transaction) => accumulator + currentTransaction.amount
  const otherTransactionsTotalAmount = otherTransactions?.reduce(transactionAmountReducer, 0)
  const transactionsChanged = transactionsWithCount?.transactions.filter(x => x.categoryId !== rule.categoryId).length
  const transactionsNotLoaded = transactionsWithCount !== null ? transactionsWithCount.totalCount - transactionsWithCount.transactions.length : 0
  const notAllTransactionsAreLoaded = transactionsNotLoaded > 0
  const canApply = !notAllTransactionsAreLoaded && categoryIdValidationResult.valid && (!applyToAll || (ruleDescriptionValidationResult.valid && isPartOfTransactionsFound))

  return <Modal show={true} closeModal={closeModal}>
    <div className={styles.VerticalSpacing}>
      <HeadingMedium>Transactie</HeadingMedium>
      <ValueList>
        <ListedValue label="Naam">{transaction.name}</ListedValue>
        <ListedValue label="Beschrijving">{transaction.description}</ListedValue>
        <ListedValue label="Datum"><FormattedDate dateValue={transaction.date}/></ListedValue>
        <ListedValue label="Bedrag">{transaction.amount}</ListedValue>
        <ListedValue label="CO2-uitstoot">{transaction.co2InKg} kg</ListedValue>
        {transaction.counterpartyIban && <ListedValue label="IBAN">{transaction.counterpartyIban}</ListedValue>}
        <ListedValue label="Categorie">{categoryName}</ListedValue>
        {transaction.excludeFromSpendingTotals && <ListedValue label="NB">Transactie uitgesloten van totalen</ListedValue>}
      </ValueList>

      {!showEditCategory && <div>
        <Button buttonStyle="primary" onClick={openEditCategory}>Categorie aanpassen</Button>
      </div>}
      {showEditCategory && <Panel>
        <HeadingSmall>Categorie aanpassen</HeadingSmall>
        <FormCategorySelect selectedItemId={rule.categoryId} onChange={setCategoryId}/>

        <Checkbox highContrast name="ApplyToAll" label="Toepassen op alle gevonden en toekomstige transacties" checked={applyToAll} onChange={() => setApplyToAll(!applyToAll)}/>
        {applyToAll && <>
          {ruleUsed !== null && <>
            {transaction.categorizationTypeAsEnum === "SystemRules" && <>
              <TextSmall color="dark-grey">Deze transacties is eerder automatisch gecategoriseerd door ons systeem vanwege de volgende tekst: "{ruleUsed.description || ruleUsed.additionalTerms}".
                Indien u kiest om de indeling te wijzigen en daarbij toekomstige transacties mee te nemen zal dit altijd voorgaan boven systeemregels.</TextSmall>
            </>}
            {transaction.categorizationTypeAsEnum === "CustomRules" && <>
              <TextSmall color="dark-grey">Deze transactie is eerder gecategoriseerd op basis van een door u ingestelde regel: <span data-hj-suppress={true}>{ruleUsed.description}</span>.</TextSmall>
              <RadioButtonGroup validationResult={{valid: true}}>
                <RadioButton name="existingRuleBehaviour" value="DeleteExistingRule" checked={existingRuleOption === "DeleteExistingRule"} label="Bestaande regel aanpassen" onChange={clickExistingRuleRadioButton}/>
                <RadioButton name="existingRuleBehaviour" value="AddHigherPriorityRule" checked={existingRuleOption === "AddHigherPriorityRule"} label="Nieuwe regel toevoegen" onChange={clickExistingRuleRadioButton}/>
              </RadioButtonGroup>
            </>}
          </>}
          {transaction.categorizationTypeAsEnum === "Manually" && <>
            Deze transactie is eerder handmatig gecategoriseerd. Een handmatig gecategoriseerde transactie zal nooit overschreven worden door automatische indeling.
          </>}
          <FormTextInput label="Naam of beschrijving" value={transactionsSettings.name} onChange={(e) => setName(e.target.value)} showValidity={true} validationResult={ruleDescriptionValidationResult}/>
        </>}
        {!isPartOfTransactionsFound && ruleDescriptionValidationResult.valid && <TextMedium color="crimson-red">De geselecteerde transactie is geen deel van de gevonden transacties. Dit dient wel zo te zijn om de wijzigingen toe te passen.</TextMedium>}

        {notAllTransactionsAreLoaded
          ? <>
            Er zijn meer dan {transactionsWithCount?.transactions.length} transacties gevonden.
          </>
          : canApply && applyToAll && <>
            {transactionsChanged !== undefined && transactionsChanged > 1 && `${transactionsChanged} transacties worden hiermee ingedeeld`}
            {transactionsChanged !== undefined && transactionsChanged === 1 && `${transactionsChanged} transactie wordt hiermee ingedeeld`}
          </>}
        <div className={styles.Buttons}>
          <Button buttonStyle="secondary" onClick={() => cancelEditCategory()}>Annuleren</Button>
          {notAllTransactionsAreLoaded
            ? <Button buttonStyle="primary" onClick={clickLoadAll}>Overige {transactionsNotLoaded} transacties laden</Button>
            : (canApply
              ? <Button buttonStyle="primary" onClick={clickApply}>Toepassen</Button>
              : null)}
        </div>
      </Panel>}

      {otherTransactions !== undefined && otherTransactionsTotalAmount !== undefined && <>
        <HeadingSmall>Soortgelijke transacties</HeadingSmall>
        {otherTransactions.length > 0
          ? <>
            <div>{otherTransactions.length} soortgelijke transacties gevonden met een totaalbedrag van <TransactionAmount amount={otherTransactionsTotalAmount}/>
            </div>
            <TransactionsList transactions={otherTransactions}/>
          </>
          : <span>Er zijn geen soortgelijke transacties gevonden</span>}
      </>}
    </div>
  </Modal>
}
